import { Dispatch, createContext, useReducer } from 'react';
import { BaseResource } from '../resources';
import { Dict } from '../types';

enum StoreActionType {
  Upsert = 'UPSERT',
  UpsertMany = 'UPSERT_MANY',
  CacheQuery = 'CACHE_QUERY',
  Delete = 'DELETE',
}

interface IAction {
  type: StoreActionType;
  target: string;
  payload: any;
}

interface IDataContext {
  dispatch: Dispatch<IAction>;
  state: Dict<Dict<BaseResource<any>>>;
}

const initialState = {
  UserResource: {},
  UserResourceCachedQuery: [],
  TutorResource: {},
  TutorResourceCachedQuery: [],
  ArticleResource: {},
  ArticleResourceCachedQuery: [],
  MediaResource: {},
  MediaResourceCachedQuery: [],
  TagResource: {},
  TagResourceCachedQuery: [],
  QuestionResource: {},
  QuestionResourceCachedQuery: [],
  EikenMaterialResource: {},
  EikenMaterialResourceCachedQuery: [],
  EikenTestResource: {},
  EikenTestResourceCachedQuery: [],
  FeedbackLevelResource: {},
  FeedbackLevelResourceCachedQuery: [],
  LocationResource: {},
  LocationResourceCachedQuery: [],
  DictionaryResource: {},
  DictionaryResourceCachedQuery: [],
  StatisticResource: {},
  StatisticResourceCachedQuery: [],
  StudentResource: {},
  StudentResourceCachedQuery: [],
  ChatResource: {},
  ChatResourceCachedQuery: [],
  SystemConfigResource: {},
  SystemConfigResourceCachedQuery: [],
  CompanyResource: {},
  CompanyResourceCachedQuery: [],
  ScenarioResource: {},
  ScenarioResourceCachedQuery: [],
  ParticipantResource: {},
  ParticipantResourceCachedQuery: [],
  CharacterResource: {},
  CharacterResourceCachedQuery: [],
};

function reducerFn(state: any, action: IAction) {
  const newState = { ...state };

  if (!Object.hasOwn(initialState, action.target)) {
    throw new Error(`Unknown target ${action.target}`);
  }

  if (action.type === StoreActionType.Upsert) {
    const id = action.payload.id;
    if (!id) throw new Error('Missing id in payload');

    newState[action.target][id] = action.payload;
  } else if (action.type === StoreActionType.UpsertMany) {
    const ids = action.payload.map((item: any) => item.id);

    if (ids.length !== action.payload.length) {
      throw new Error('Missing id in some payload items');
    }

    for (const item of action.payload) newState[action.target][item.id] = item;
  } else if (action.type === StoreActionType.CacheQuery) {
    newState[action.target + 'CachedQuery'] = action.payload;
  } else if (action.type === StoreActionType.Delete) {
    const id = action.payload;

    delete newState[action.target][id];
    newState[action.target + 'CachedQuery'] = newState[action.target + 'CachedQuery'].filter(
      (itemId: any) => itemId !== id
    );
  }

  return newState;
}

const DataContext = createContext({
  state: initialState,
  dispatch: (() => { }) as Dispatch<IAction>,
});

type DataProviderProps = {
  children: React.ReactNode;
};

const DataProvider = ({ children }: DataProviderProps) => {
  const [state, dispatch] = useReducer(reducerFn, initialState);

  BaseResource.setStore({ state, dispatch });

  return <DataContext.Provider value={{ state, dispatch }}>{children}</DataContext.Provider>;
};

export { DataContext, DataProvider, StoreActionType };
export type { IDataContext };
